import React, { useState } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';

const CustomFieldsManager = ({ customFields, onUpdateFields }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [fieldData, setFieldData] = useState({
    fieldName: '',
    fieldLabel: '',
    fieldType: 'text',
    required: false,
    placeholder: '',
    helpText: '',
    options: [],
    validation: {},
    displayOrder: 0,
    showInProfile: false
  });

  const resetForm = () => {
    setFieldData({
      fieldName: '',
      fieldLabel: '',
      fieldType: 'text',
      required: false,
      placeholder: '',
      helpText: '',
      options: [],
      validation: {},
      displayOrder: 0,
      showInProfile: false
    });
    setEditingIndex(null);
  };

  const handleAdd = () => {
    resetForm();
    setShowForm(true);
  };

  const handleEdit = (field, index) => {
    setFieldData(field);
    setEditingIndex(index);
    setShowForm(true);
  };

  const handleDelete = (index) => {
    if (window.confirm('Are you sure you want to delete this custom field?')) {
      const newFields = customFields.filter((_, i) => i !== index);
      onUpdateFields(newFields);
    }
  };

  const handleSave = () => {
    // Basic validation
    if (!fieldData.fieldLabel) {
      alert('Field label is required');
      return;
    }

    // Generate field name from label if not provided
    let fieldName = fieldData.fieldName;
    if (!fieldName) {
      fieldName = fieldData.fieldLabel.toLowerCase().replace(/[^a-z0-9]/g, '_');
    }

    const newField = {
      ...fieldData,
      fieldName,
      displayOrder: fieldData.displayOrder || customFields.length
    };

    let newFields;
    if (editingIndex !== null) {
      // Update existing field
      newFields = customFields.map((field, index) => 
        index === editingIndex ? newField : field
      );
    } else {
      // Add new field
      newFields = [...customFields, newField];
    }

    onUpdateFields(newFields);
    setShowForm(false);
    resetForm();
  };

  const getFieldTypeIcon = (type) => {
    const icons = {
      text: '📝',
      number: '🔢',
      email: '📧',
      phone: '📞',
      select: '📋',
      textarea: '📄',
      checkbox: '☑️',
      date: '📅'
    };
    return icons[type] || '📝';
  };

  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="h6">Custom Registration Fields</Typography>
        {customFields.length > 0 && (
          <Typography variant="caption" sx={{ ml: 2, color: 'text.secondary' }}>
            ({customFields.length} fields)
          </Typography>
        )}
      </AccordionSummary>
      <AccordionDetails>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Define additional fields that will appear in the registration form for this member type.
        </Typography>
        
        {customFields.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
            No custom fields defined. Click "Add Field" to create one.
          </Typography>
        ) : (
          <List>
            {customFields.map((field, index) => (
              <ListItem key={index} divider>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span>{getFieldTypeIcon(field.fieldType)}</span>
                      {field.fieldLabel}
                      {field.required && <span style={{ color: 'red' }}>*</span>}
                    </Box>
                  }
                  secondary={`${field.fieldType} field - ${field.required ? 'Required' : 'Optional'}`}
                />
                <ListItemSecondaryAction>
                  <IconButton size="small" onClick={() => handleEdit(field, index)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => handleDelete(index)}>
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}
        
        <Button
          startIcon={<AddIcon />}
          onClick={handleAdd}
          variant="outlined"
          sx={{ mt: 2 }}
        >
          Add Custom Field
        </Button>

        {/* Custom Field Form Dialog */}
        <Dialog open={showForm} onClose={() => setShowForm(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            {editingIndex !== null ? 'Edit Custom Field' : 'Add Custom Field'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid size={6}>
                <TextField
                  fullWidth
                  label="Field Name"
                  value={fieldData.fieldName}
                  onChange={(e) => setFieldData(prev => ({ ...prev, fieldName: e.target.value }))}
                  placeholder="e.g., companyName"
                  helperText="Internal field name (no spaces)"
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  fullWidth
                  label="Field Label *"
                  value={fieldData.fieldLabel}
                  onChange={(e) => setFieldData(prev => ({ ...prev, fieldLabel: e.target.value }))}
                  placeholder="e.g., Company Name"
                  helperText="Label shown to users"
                />
              </Grid>
              <Grid size={6}>
                <FormControl fullWidth>
                  <InputLabel>Field Type</InputLabel>
                  <Select
                    value={fieldData.fieldType}
                    onChange={(e) => setFieldData(prev => ({ ...prev, fieldType: e.target.value }))}
                    label="Field Type"
                  >
                    <MenuItem value="text">Text</MenuItem>
                    <MenuItem value="number">Number</MenuItem>
                    <MenuItem value="email">Email</MenuItem>
                    <MenuItem value="phone">Phone</MenuItem>
                    <MenuItem value="select">Dropdown</MenuItem>
                    <MenuItem value="textarea">Text Area</MenuItem>
                    <MenuItem value="checkbox">Checkbox</MenuItem>
                    <MenuItem value="date">Date</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={fieldData.required}
                      onChange={(e) => setFieldData(prev => ({ ...prev, required: e.target.checked }))}
                    />
                  }
                  label="Required Field"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={fieldData.showInProfile}
                      onChange={(e) => setFieldData(prev => ({ ...prev, showInProfile: e.target.checked }))}
                    />
                  }
                  label="Show in Profile"
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Placeholder Text"
                  value={fieldData.placeholder}
                  onChange={(e) => setFieldData(prev => ({ ...prev, placeholder: e.target.value }))}
                  placeholder="e.g., Enter your company name"
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Help Text"
                  value={fieldData.helpText}
                  onChange={(e) => setFieldData(prev => ({ ...prev, helpText: e.target.value }))}
                  placeholder="Additional help or instructions for users"
                  multiline
                  rows={2}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowForm(false)}>Cancel</Button>
            <Button onClick={handleSave} variant="contained">
              {editingIndex !== null ? 'Update' : 'Add'} Field
            </Button>
          </DialogActions>
        </Dialog>
      </AccordionDetails>
    </Accordion>
  );
};

export default CustomFieldsManager;